// Google Places API (New) client for the address-selection screen.
// Uses plain fetch, NOT the freefit http singleton — different host, headers,
// and no freefit interceptors/retries should apply to Google calls.
const PLACES_BASE_URL = "https://places.googleapis.com/v1";
// Reverse geocoding is not part of Places API (New) — it lives in the Geocoding API,
// which is a separate product that has to be enabled for the same key.
const GEOCODE_URL = "https://maps.googleapis.com/maps/api/geocode/json";
const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

const assertApiKey = () => {
  if (!API_KEY) {
    throw new Error("VITE_GOOGLE_PLACES_API_KEY is missing — add it to .env (Places API (New) must be enabled for the key)");
  }
};

// Throws the {code, mess} shape swalDialog understands.
const throwResponseError = async (response) => {
  let body;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  console.error("Google Places request failed:", response.status, body);
  throw { code: response.status, mess: body?.error?.message };
};

const findComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type))?.longText ?? "";

// The Geocoding API predates Places (New) and answers in snake_case with `long_name`
// instead of `addressComponents`/`longText`, so it needs its own reader.
const findLegacyComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type))?.long_name ?? "";

// Israeli addresses don't always carry a "locality"; same ladder getPlaceDetails uses.
const pickCity = (find, components) =>
  find(components, "locality") ||
  find(components, "sublocality_level_1") ||
  find(components, "administrative_area_level_2");

class PlacesAPI {
  /**
   * Fetch address suggestions (Israel only, Hebrew).
   * @param {string} input - The user's partial address query
   * @param {string} sessionToken - Autocomplete session token (UUID), shared with the details call
   * @param {AbortSignal} [signal] - Cancels the request when a newer query supersedes it
   * @returns {Promise<Array<{placeId: string, mainText: string, secondaryText: string}>>}
   */
  async autocomplete(input, sessionToken, signal) {
    assertApiKey();
    const response = await fetch(`${PLACES_BASE_URL}/places:autocomplete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": API_KEY,
      },
      body: JSON.stringify({
        input,
        sessionToken,
        languageCode: "he",
        includedRegionCodes: ["il"],
      }),
      signal,
    });

    if (!response.ok) await throwResponseError(response);

    const data = await response.json();
    return (data.suggestions ?? [])
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .map((p) => ({
        placeId: p.placeId,
        mainText: p.structuredFormat?.mainText?.text ?? p.text?.text ?? "",
        secondaryText: p.structuredFormat?.secondaryText?.text ?? "",
      }));
  }

  /**
   * Resolve a suggestion to structured address fields + coordinates.
   * @param {string} placeId - Place ID from an autocomplete suggestion
   * @param {string} sessionToken - The same session token used for the autocomplete calls
   * @returns {Promise<{street: string, houseNumber: string, city: string, latitude: number, longitude: number, formattedAddress: string}>}
   */
  async getPlaceDetails(placeId, sessionToken) {
    assertApiKey();
    const params = new URLSearchParams({ languageCode: "he", sessionToken });
    const response = await fetch(`${PLACES_BASE_URL}/places/${placeId}?${params}`, {
      headers: {
        "X-Goog-Api-Key": API_KEY,
        "X-Goog-FieldMask": "location,addressComponents,formattedAddress",
      },
    });

    if (!response.ok) await throwResponseError(response);

    const data = await response.json();
    const components = data.addressComponents;
    return {
      street: findComponent(components, "route"),
      houseNumber: findComponent(components, "street_number"),
      city: pickCity(findComponent, components),
      latitude: data.location?.latitude,
      longitude: data.location?.longitude,
      formattedAddress: data.formattedAddress ?? "",
    };
  }

  /**
   * Turn coordinates into structured address fields (reverse geocoding).
   *
   * Uses the Geocoding API, not Places (New), which has no reverse lookup — so
   * **the Geocoding API must be enabled for VITE_GOOGLE_PLACES_API_KEY**. If it
   * isn't, Google answers 200 with status REQUEST_DENIED, which this turns into a
   * throw for the caller to swallow.
   *
   * @param {{latitude: number, longitude: number}} coords
   * @returns {Promise<{street: string, houseNumber: string, city: string, formattedAddress: string}>}
   */
  async reverseGeocode({ latitude, longitude }) {
    assertApiKey();
    const params = new URLSearchParams({
      latlng: `${latitude},${longitude}`,
      language: "he",
      key: API_KEY,
    });
    const response = await fetch(`${GEOCODE_URL}?${params}`);

    if (!response.ok) await throwResponseError(response);

    const data = await response.json();
    // The Geocoding API reports failures in the body with a 200, unlike Places.
    if (data.status !== "OK" || !data.results?.length) {
      console.error("Reverse geocode failed:", data.status, data.error_message);
      throw { code: data.status, mess: data.error_message };
    }

    // results[0] is the most specific match — a street address where one exists.
    const best = data.results[0];
    const components = best.address_components;
    return {
      street: findLegacyComponent(components, "route"),
      houseNumber: findLegacyComponent(components, "street_number"),
      city: pickCity(findLegacyComponent, components),
      formattedAddress: best.formatted_address ?? "",
    };
  }
}

const placesAPI = new PlacesAPI();
export default placesAPI;
